import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import { Mail, MapPin, Instagram, Facebook, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CONTACT_INFO, SOCIAL_LINKS } from "@/config/constants";
import { EMAILJS_CONFIG, isEmailJSConfigured } from "@/config/emailjs";

export const ContactSection = () => {
  const ref = useRef(null);
  const formRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  // Formatação dinâmica de telefone para melhor UX
  const formatPhone = (value) => {
    if (!value) return "";
    const phoneNumber = value.replace(/\D/g, "");
    const limitedNumber = phoneNumber.slice(0, 11);

    if (limitedNumber.length <= 2)
      return limitedNumber.length > 0 ? `(${limitedNumber}` : limitedNumber;
    if (limitedNumber.length <= 6)
      return `(${limitedNumber.slice(0, 2)}) ${limitedNumber.slice(2)}`;
    if (limitedNumber.length <= 10)
      return `(${limitedNumber.slice(0, 2)}) ${limitedNumber.slice(2, 6)}-${limitedNumber.slice(6)}`;
    return `(${limitedNumber.slice(0, 2)}) ${limitedNumber.slice(2, 7)}-${limitedNumber.slice(7)}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      setFormData({ ...formData, [name]: formatPhone(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // Lógica de envio via EmailJS com feedback visual de estado (toast/loading)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Por favor, preencha todos os campos obrigatórios.");
      setIsSubmitting(false);
      return;
    }

    if (!isEmailJSConfigured()) {
      toast.warning("EmailJS não está configurado.");
      setIsSubmitting(false);
      return;
    }

    try {
      await emailjs.sendForm(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        formRef.current,
        EMAILJS_CONFIG.PUBLIC_KEY,
      );
      toast.success("Mensagem enviada com sucesso!");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Erro ao enviar mensagem.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Itens de Contato: Cards com fundo em baixa opacidade e hover dinâmico
  const ContactItems = ({ className = "" }) => (
    <div className={`space-y-6 ${className}`}>
      {/* WhatsApp */}
      <a
        href={CONTACT_INFO.phoneLink}
        className="flex items-center gap-4 group"
      >
        <div className="w-12 h-12 bg-gold/10 flex items-center justify-center group-hover:bg-gold transition-colors duration-300">
          <Send className="w-5 h-5 text-gold group-hover:text-darkblue transition-colors duration-300" />
        </div>
        <div>
          <div className="text-sm text-slate-500">WhatsApp</div>
          <div className="font-semibold text-darkblue">
            {CONTACT_INFO.phone}
          </div>
        </div>
      </a>

      {/* Email */}
      <a
        href={CONTACT_INFO.emailLink}
        className="flex items-center gap-4 group"
      >
        <div className="w-12 h-12 bg-gold/10 flex items-center justify-center group-hover:bg-gold transition-colors duration-300">
          <Mail className="w-5 h-5 text-gold group-hover:text-darkblue transition-colors duration-300" />
        </div>
        <div>
          <div className="text-sm text-slate-500">E-mail</div>
          <div className="font-semibold text-darkblue">
            {CONTACT_INFO.email}
          </div>
        </div>
      </a>

      {/* Endereço */}
      <div className="flex items-start gap-4 text-left">
        <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0">
          <MapPin className="w-5 h-5 text-gold" />
        </div>
        <div>
          <div className="text-sm text-slate-500">Endereço</div>
          <div className="font-semibold text-darkblue">
            {CONTACT_INFO.address.street}
            <br />
            {CONTACT_INFO.address.city}, {CONTACT_INFO.address.cep}
          </div>
        </div>
      </div>
    </div>
  );

  // Redes Sociais: Estilo corporativo com inversão de cores no hover
  const SocialLinks = ({ className = "" }) => (
    <div className={`flex gap-4 ${className}`}>
      <a
        href={SOCIAL_LINKS.instagram}
        target="_blank"
        rel="noopener noreferrer"
        className="w-12 h-12 bg-darkblue flex items-center justify-center hover:bg-gold transition-colors duration-300 group"
      >
        <Instagram className="w-5 h-5 text-white group-hover:text-darkblue transition-colors duration-300" />
      </a>
      <a
        href={SOCIAL_LINKS.facebook}
        target="_blank"
        rel="noopener noreferrer"
        className="w-12 h-12 bg-darkblue flex items-center justify-center hover:bg-gold transition-colors duration-300 group"
      >
        <Facebook className="w-5 h-5 text-white group-hover:text-darkblue transition-colors duration-300" />
      </a>
    </div>
  );

  return (
    <section id="contato" ref={ref} className="py-16 md:py-32 bg-white">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20">
          {/* Coluna de Texto: Hierarquia com tracking largo no subtítulo */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold mb-4 block">
              Entre em contato
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-darkblue leading-tight mb-6 tracking-tight">
              Vamos elevar o <br />
              padrão da sua <span className="text-gold">gestão.</span>
            </h2>

            <p className="text-slate-600 text-base md:text-lg leading-relaxed mb-8 font-light">
              Estamos prontos para entender suas necessidades e apresentar as
              melhores soluções para sua empresa. Entre em contato conosco.
            </p>

            {/* Renderização de Infos e Redes (Desktop) */}
            <ContactItems className="hidden lg:block" />
            <div className="hidden lg:flex mt-10 pt-10 border-t border-slate-200 items-center gap-6">
              <div className="text-[10px] uppercase tracking-[0.4em] text-slate-400 font-bold">
                Siga-nos
              </div>
              <SocialLinks />
            </div>
          </motion.div>

          {/* Coluna do Formulário: Design limpo com inputs de borda suave */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              className="bg-slate-50 p-6 sm:p-8 md:p-10"
            >
              <h3 className="font-heading text-xl md:text-2xl font-bold text-darkblue mb-6 md:mb-8">
                Envie uma mensagem
              </h3>

              <div className="space-y-5 md:space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Nome *
                  </label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Seu nome completo"
                    className="w-full h-12 bg-white border-slate-200 focus:border-gold focus:ring-gold"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    E-mail *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="seu@email.com"
                    className="w-full h-12 bg-white border-slate-200 focus:border-gold focus:ring-gold"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Telefone *
                  </label>
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(00) 00000-0000"
                    className="w-full h-12 bg-white border-slate-200 focus:border-gold focus:ring-gold"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Mensagem *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Como podemos ajudar?"
                    rows={5}
                    className="w-full bg-white border-slate-200 text-slate-900 focus:border-gold focus:ring-1 focus:ring-gold resize-none transition-all"
                    required
                  />
                </div>

                {/* Botão de Envio: Efeito Glow, Shimmer e Tracking Premium */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="group relative w-full h-12 md:h-14 bg-gold text-darkblue font-semibold uppercase tracking-[0.25em] text-[11px] overflow-hidden transition-all duration-500 hover:bg-darkblue hover:text-white disabled:opacity-50 shadow-[0_15px_40px_rgba(212,175,55,0.25)] active:scale-[0.98]"
                >
                  <span className="relative z-10 flex items-center justify-center gap-3">
                    {isSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send
                          size={16}
                          className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:rotate-12"
                        />
                        Enviar Mensagem
                      </>
                    )}
                  </span>
                  {/* Shimmer Effect para acabamento de alta qualidade */}
                  <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:animate-[shimmer_1.4s_infinite] pointer-events-none" />
                </Button>
              </div>
            </form>

            {/* Exibição Mobile: Organização das infos abaixo do formulário */}
            <div className="lg:hidden mt-10 space-y-8">
              <ContactItems />
              <div className="pt-8 border-t border-slate-200 flex flex-col gap-4">
                <div className="text-[10px] uppercase tracking-[0.4em] text-slate-400 font-bold">
                  Siga-nos
                </div>
                <SocialLinks />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
